/**
 * Session and Auth Management Service
 * Handles session IDs, tokens, and auth-related storage
 */

/**
 * Generate a unique session ID
 * @returns {string} Unique session identifier
 */
export const generateSessionId = () => {
  const timestamp = Date.now().toString().slice(-5);
  const randomNum = Math.floor(Math.random() * 100000)
    .toString()
    .padStart(5, '0');
  const sessionId = timestamp + randomNum;
  return sessionId;
};

/**
 * Get the stored authentication token
 * @returns {string|null} Authentication token or null
 */
export const getToken = () => {
  return localStorage.getItem('token');
};

/**
 * Store the authentication token
 * @param {string} token - Authentication token to store
 */
export const setToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
  }
};

/**
 * Get the stored session ID
 * @returns {string|null} Session ID or null
 */
export const getSessionId = () => {
  let sessionId = localStorage.getItem('session_id');

  if (!sessionId) {
    sessionId = setSessionId(); // auto-create if missing
  }

  return sessionId;
};

/**
 * Store the session ID
 * @param {string} sessionId - Session ID to store
 */
export const setSessionId = (sessionId = null) => {
  if (!sessionId) {
    sessionId = generateSessionId();
  }

  localStorage.setItem('session_id', sessionId);
  return sessionId;
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if token exists
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * Clear all authentication data
 */
export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('session_id');
};

/**
 * Initialize session after login
 * @param {string} token - Authentication token
 * @returns {string} Generated session ID
 */
export const initializeSession = (token) => {
  setToken(token);
  const sessionId = setSessionId();
  return sessionId;
};
